import {AllocationMethod} from '@sollapay/enums';
import {normalizeReferenceCode, isValidReferenceCode, parseMoney} from '@sollapay/utils';
import {Money} from '@sollapay/utils/money';

// Re-export from utils for convenience
export {normalizeReferenceCode, isValidReferenceCode};

export function isValidUUID(value: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

export function agorotToNis(amountInAgorot: number): string {
  return Money.from(amountInAgorot).divide(100).toString();
}

export function calculateAllocationConfidence(method: AllocationMethod): number {
  switch (method) {
    case AllocationMethod.REFERENCE_CODE:
      return 100;
    case AllocationMethod.MANUAL:
      return 100;
    case AllocationMethod.PROVIDER_PAYMENT_ID:
      return 100;
    case AllocationMethod.ANAGRAM_REFERENCE_CODE:
      return 80;
    case AllocationMethod.PARTIAL_ANAGRAM_REFERENCE_CODE:
      return 60;
    default:
      return 50;
  }
}

export interface AppliedAndExcessAmountsInput {
  instructionAmount: string;
  /** Sum already counted toward the instruction from other settled/accepted payins. */
  alreadyAllocatedAmount: string;
  /** The amount of this payin/credit being allocated. */
  thisAmount: string;
}

export interface AppliedAndExcessAmounts {
  /** Portion of thisAmount that counts toward the instruction's remaining balance. */
  appliedAmount: string;
  /** Portion of thisAmount beyond what the instruction still owes (an overpayment). */
  excessAmount: string;
  /** Whether the instruction is fully covered once appliedAmount is counted. */
  isFullyPaid: boolean;
}

/**
 * Splits an incoming amount into what counts toward an instruction's remaining balance
 * vs. what's an overpayment — shared by PayinAllocationPolicy (create path) and
 * PayinSettlementService (confirm path) so both settle the same money the same way.
 *
 * isFullyPaid is derived from the same remaining/applied values computed here (applied
 * reaches remaining only when thisAmount covers or exceeds it) rather than callers
 * separately re-parsing alreadyAllocatedAmount/thisAmount to compare against
 * instructionAmount again.
 */
export function calculateAppliedAndExcessAmounts(
  input: AppliedAndExcessAmountsInput
): AppliedAndExcessAmounts {
  const {instructionAmount, alreadyAllocatedAmount, thisAmount} = input;

  const remaining = Money.max(
    parseMoney(instructionAmount).subtract(alreadyAllocatedAmount),
    Money.zero()
  );
  const thisMoney = parseMoney(thisAmount);
  const applied = thisMoney.clamp(Money.zero(), remaining);
  const excess = thisMoney.subtract(applied);

  return {
    appliedAmount: applied.toString(),
    excessAmount: excess.toString(),
    isFullyPaid: applied.gte(remaining)
  };
}

/**
 * Frees a rejected payin's unique statement identifiers. A statement-created payin (tx id in both
 * columns) is renamed; a provider payin keeps its id so a later webhook still finds it.
 */
export function releaseStatementIdentifiers(payin: {
  id: string;
  providerPaymentId: string;
  bankStatementTxId: string | null;
}) {
  const createdFromStatement = payin.providerPaymentId === payin.bankStatementTxId;

  return {
    providerPaymentId: createdFromStatement
      ? `${payin.providerPaymentId}#rejected-${payin.id}`
      : payin.providerPaymentId,
    bankStatementTxId: null
  };
}
